import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useRecoilState,
  useRecoilCallback,
  useResetRecoilState,
} from "recoil";
import {
  defaultToolbarFilterState,
  DEFAULT_CREATE_UPDATE_FILTER,
  persistTicketToolbarFilters,
  readTicketToolbarFilters,
  ensureDefaultCreatedDateFilter,
  TICKET_TOOLBAR_FILTERS_CLEARED_EVENT,
  type TicketToolbarFilterState,
  type CreateUpdateFilter,
} from "@sd/lib/ticketListFilterStorage";
import { ticketToolbarFiltersAtomFamily } from "@sd/state/ticketToolbarFiltersState";
import type { CreatedDateFilterState } from "@sd/lib/ticketCreatedDateFilter";

export interface TicketToolbarFiltersRecoil {
  search: string;
  setSearch: (v: string) => void;
  statusFilters: string[];
  setStatusFilters: (v: string[]) => void;
  priorityFilters: string[];
  setPriorityFilters: (v: string[]) => void;
  categoryFilters: string[];
  setCategoryFilters: (v: string[]) => void;
  typeFilter: string;
  setTypeFilter: (v: string) => void;
  projectFilters: string[];
  setProjectFilters: (v: string[]) => void;
  assigneeFilters: string[];
  setAssigneeFilters: (v: string[]) => void;
  requesterFilters: string[];
  setRequesterFilters: (v: string[]) => void;
  createdDateFilter: CreatedDateFilterState;
  setCreatedDateFilter: (v: CreatedDateFilterState) => void;
  vipFilter: string;
  setVipFilter: (v: string) => void;
  slaFilter: string;
  setSlaFilter: (v: string) => void;
  moduleFilter: string;
  setModuleFilter: (v: string) => void;
  createUpdateFilter: CreateUpdateFilter;
  setCreateUpdateFilter: (v: CreateUpdateFilter) => void;
  filters: TicketToolbarFilterState;
  applyPartial: (partial: Partial<TicketToolbarFilterState>) => void;
  resetToolbarFilters: () => void;
  resetToolbarScope: (scope: string) => void;
  applyDefaultAtScope: (scope: string) => void;
}

function sameStringArray(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

function sameCreatedDate(
  a: CreatedDateFilterState,
  b: CreatedDateFilterState
): boolean {
  return a.from === b.from && a.to === b.to;
}

export function useTicketToolbarFiltersRecoil(
  scope: string,
  /** If true: restore from sessionStorage on mount; persist on every change. */
  persistOnRefresh = false,
  /** Optional sessionStorage scope (defaults to Recoil scope). */
  storageScope?: string
): TicketToolbarFiltersRecoil {
  const persistStorageScope = storageScope ?? scope;
  const [filters, setFilters] = useRecoilState(
    ticketToolbarFiltersAtomFamily(scope)
  );
  const resetCurrent = useResetRecoilState(
    ticketToolbarFiltersAtomFamily(scope)
  );
  const [storageReady, setStorageReady] = useState(false);
  /** Skip persist while Recoil may still hold pre-restore defaults (F5 race). */
  const skipPersistAfterRestoreRef = useRef(false);

  // Older Recoil/session state may omit createUpdateFilter — keep atom complete.
  useEffect(() => {
    if (
      filters.createUpdateFilter === "all" ||
      filters.createUpdateFilter === "on_create" ||
      filters.createUpdateFilter === "on_update"
    ) {
      return;
    }
    setFilters(prev => ({
      ...prev,
      createUpdateFilter: DEFAULT_CREATE_UPDATE_FILTER,
    }));
  }, [filters.createUpdateFilter, setFilters]);

  // Restore saved filters once persist mode is active (after auth resolves).
  useEffect(() => {
    if (!persistOnRefresh) {
      setStorageReady(false);
      skipPersistAfterRestoreRef.current = false;
      return;
    }

    const saved = readTicketToolbarFilters(persistStorageScope);
    // Prevent the persist effect from writing Recoil defaults over sessionStorage
    // before the restored filters commit (that wipe made F5 look like "filters cleared").
    skipPersistAfterRestoreRef.current = true;
    if (saved) {
      const restored = ensureDefaultCreatedDateFilter(saved);
      setFilters(restored);
      // Lock restored filters into storage immediately (survives Strict Mode remount).
      persistTicketToolbarFilters(persistStorageScope, restored);
    }
    setStorageReady(true);
  }, [persistOnRefresh, persistStorageScope, setFilters]);

  // Logout clears sessionStorage; reset in-memory Recoil so filters aren't re-saved.
  useEffect(() => {
    if (!persistOnRefresh) return;

    const handleFiltersCleared = () => {
      resetCurrent();
      setStorageReady(false);
      skipPersistAfterRestoreRef.current = false;
    };

    window.addEventListener(
      TICKET_TOOLBAR_FILTERS_CLEARED_EVENT,
      handleFiltersCleared
    );
    return () =>
      window.removeEventListener(
        TICKET_TOOLBAR_FILTERS_CLEARED_EVENT,
        handleFiltersCleared
      );
  }, [persistOnRefresh, resetCurrent]);

  // Persist only after restore finishes — never overwrite storage with Recoil defaults on refresh.
  useEffect(() => {
    if (!persistOnRefresh || !storageReady) return;
    if (skipPersistAfterRestoreRef.current) {
      skipPersistAfterRestoreRef.current = false;
      return;
    }
    persistTicketToolbarFilters(persistStorageScope, filters);
  }, [persistOnRefresh, storageReady, persistStorageScope, filters]);

  // Stable setters — must NOT recreate when `filters` changes (effects depend on these).
  const setSearch = useCallback(
    (v: string) => {
      setFilters(prev => (prev.search === v ? prev : { ...prev, search: v }));
    },
    [setFilters]
  );
  const setStatusFilters = useCallback(
    (v: string[]) => {
      setFilters(prev =>
        sameStringArray(prev.statusFilters, v)
          ? prev
          : { ...prev, statusFilters: v }
      );
    },
    [setFilters]
  );
  const setPriorityFilters = useCallback(
    (v: string[]) => {
      setFilters(prev =>
        sameStringArray(prev.priorityFilters, v)
          ? prev
          : { ...prev, priorityFilters: v }
      );
    },
    [setFilters]
  );
  const setCategoryFilters = useCallback(
    (v: string[]) => {
      setFilters(prev =>
        sameStringArray(prev.categoryFilters, v)
          ? prev
          : { ...prev, categoryFilters: v }
      );
    },
    [setFilters]
  );
  const setTypeFilter = useCallback(
    (v: string) => {
      setFilters(prev =>
        prev.typeFilter === v ? prev : { ...prev, typeFilter: v }
      );
    },
    [setFilters]
  );
  const setProjectFilters = useCallback(
    (v: string[]) => {
      setFilters(prev =>
        sameStringArray(prev.projectFilters, v)
          ? prev
          : { ...prev, projectFilters: v }
      );
    },
    [setFilters]
  );
  const setAssigneeFilters = useCallback(
    (v: string[]) => {
      setFilters(prev =>
        sameStringArray(prev.assigneeFilters, v)
          ? prev
          : { ...prev, assigneeFilters: v }
      );
    },
    [setFilters]
  );
  const setRequesterFilters = useCallback(
    (v: string[]) => {
      setFilters(prev =>
        sameStringArray(prev.requesterFilters, v)
          ? prev
          : { ...prev, requesterFilters: v }
      );
    },
    [setFilters]
  );
  const setCreatedDateFilter = useCallback(
    (v: CreatedDateFilterState) => {
      setFilters(prev =>
        sameCreatedDate(prev.createdDateFilter, v)
          ? prev
          : { ...prev, createdDateFilter: v }
      );
    },
    [setFilters]
  );
  const setVipFilter = useCallback(
    (v: string) => {
      setFilters(prev =>
        prev.vipFilter === v ? prev : { ...prev, vipFilter: v }
      );
    },
    [setFilters]
  );
  const setSlaFilter = useCallback(
    (v: string) => {
      setFilters(prev =>
        prev.slaFilter === v ? prev : { ...prev, slaFilter: v }
      );
    },
    [setFilters]
  );
  const setModuleFilter = useCallback(
    (v: string) => {
      setFilters(prev =>
        prev.moduleFilter === v ? prev : { ...prev, moduleFilter: v }
      );
    },
    [setFilters]
  );
  const setCreateUpdateFilter = useCallback(
    (v: CreateUpdateFilter) => {
      setFilters(prev =>
        prev.createUpdateFilter === v
          ? prev
          : { ...prev, createUpdateFilter: v }
      );
    },
    [setFilters]
  );

  const applyPartial = useCallback(
    (partial: Partial<TicketToolbarFilterState>) => {
      setFilters(prev => ({ ...prev, ...partial }));
    },
    [setFilters]
  );

  const resetToolbarFilters = useCallback(() => {
    // Set defaults explicitly — Recoil reset + follow-up setFilters(prev => …)
    // can race and re-apply the pre-clear state (VIP / On Create stuck).
    // Write defaults directly (don't clear storage first — empty window breaks F5 restore).
    const defaults = defaultToolbarFilterState();
    setFilters(defaults);
    if (persistOnRefresh) {
      persistTicketToolbarFilters(persistStorageScope, defaults);
    }
  }, [setFilters, persistOnRefresh, persistStorageScope]);

  const resetToolbarScope = useRecoilCallback(
    ({ reset }) =>
      (scopeKey: string) => {
        reset(ticketToolbarFiltersAtomFamily(scopeKey));
      },
    []
  );

  const applyDefaultAtScope = useRecoilCallback(
    ({ set }) =>
      (scopeKey: string) => {
        set(
          ticketToolbarFiltersAtomFamily(scopeKey),
          defaultToolbarFilterState()
        );
      },
    []
  );

  return useMemo(
    (): TicketToolbarFiltersRecoil => ({
      search: filters.search,
      setSearch,
      statusFilters: filters.statusFilters,
      setStatusFilters,
      priorityFilters: filters.priorityFilters,
      setPriorityFilters,
      categoryFilters: filters.categoryFilters,
      setCategoryFilters,
      typeFilter: filters.typeFilter,
      setTypeFilter,
      projectFilters: filters.projectFilters,
      setProjectFilters,
      assigneeFilters: filters.assigneeFilters,
      setAssigneeFilters,
      requesterFilters: filters.requesterFilters,
      setRequesterFilters,
      createdDateFilter: filters.createdDateFilter,
      setCreatedDateFilter,
      vipFilter: filters.vipFilter,
      setVipFilter,
      slaFilter: filters.slaFilter,
      setSlaFilter,
      moduleFilter: filters.moduleFilter,
      setModuleFilter,
      createUpdateFilter:
        filters.createUpdateFilter ?? DEFAULT_CREATE_UPDATE_FILTER,
      setCreateUpdateFilter,
      filters,
      applyPartial,
      resetToolbarFilters,
      resetToolbarScope,
      applyDefaultAtScope,
    }),
    [
      filters,
      setSearch,
      setStatusFilters,
      setPriorityFilters,
      setCategoryFilters,
      setTypeFilter,
      setProjectFilters,
      setAssigneeFilters,
      setRequesterFilters,
      setCreatedDateFilter,
      setVipFilter,
      setSlaFilter,
      setModuleFilter,
      setCreateUpdateFilter,
      applyPartial,
      resetToolbarFilters,
      resetToolbarScope,
      applyDefaultAtScope,
    ]
  );
}
